import type { ReactNode } from 'react';

import classNames from 'classnames';

import { Icon } from '../../../Icon';
import { Tooltip } from '../../../Tooltip';
import { Button } from '../../../Button';
import { FilePreview } from '../../../FilePreview';
import { Box } from '../../../Box';
import { Grid } from '../../../Grid';
import { Paper } from '../../../Paper';
import { CircularProgress } from '../../../CircularProgress';
import { Typography } from '../../../Typography';
import { EllipsisText } from '../../../EllipsisText';
import type { FileInputViewProps } from '../FileInputView.interfaces';
import useStyles from '../FileInput.styles';

interface FileInputFieldProps extends FileInputViewProps {
  cacheFileName: string;
  label: string;
  placeholder: string;
  invalidFileName: string;
  hasValidationError: boolean;
  onOpen: () => void;
}

export const FileInputField = ({
  value,
  disabled,
  fullWidth,
  withZoom,
  inputId,
  testId,
  inputLabel,
  errorDisplay,
  finalError,
  isLoading,
  isDisabled,
  inputProps,
  getRootProps,
  getInputProps,
  onLoad,
  onRemove,
  cacheFileName,
  label,
  placeholder,
  invalidFileName,
  hasValidationError,
  onOpen,
}: FileInputFieldProps) => {
  const { classes } = useStyles();

  let inputText: ReactNode = placeholder;

  if (isLoading) {
    inputText = 'Загрузка...';
  } else if (value) {
    inputText =
      (typeof value.source !== 'string' && value.source.name) ||
      (typeof value.value === 'string' && (cacheFileName || label));
  } else if (invalidFileName) {
    inputText = invalidFileName;
  }

  const getInputTextColor = () => {
    if (isDisabled) {
      return 'text.light.secondary';
    }

    if (hasValidationError || value) {
      return 'text.light.primary';
    }

    return 'text.light.tertirary';
  };

  const inputVariantDropzone = (
    <div
      {...getRootProps({ className: 'dropzone aqa_file_input_dropzone' })}
      data-testid={testId}
    >
      <input
        id={inputId}
        {...inputProps}
        {...getInputProps()}
        data-testid={`${testId}-input`}
      />
      <div
        className={classNames(
          classes.dropzone,
          finalError && classes.error,
          isDisabled && classes.disabled,
        )}
      >
        <Typography
          variant="body2"
          color={getInputTextColor()}
          className={classes.inputText}
        >
          {inputText}
        </Typography>
      </div>
    </div>
  );

  const inputVariantButton = (
    <Button
      variant="secondary"
      size="large"
      onClick={onOpen}
      disabled={disabled}
      testId={`${testId}-button`}
      icon={
        isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Icon name="attach" size="24" />
        )
      }
    />
  );

  return (
    <Box className={classNames(classes.fileInput, 'aqa_file_input')}>
      {fullWidth ? (
        <Box>
          {inputLabel}
          <Box display="flex" gap="20">
            <Box flexGrow={1} maxWidth="calc(100% - 76px)">
              {inputVariantDropzone}
            </Box>
            <Box>{inputVariantButton}</Box>
          </Box>
        </Box>
      ) : (
        <Grid container spacing="20">
          <Grid item xs={9} md={8}>
            {inputLabel}
            {inputVariantDropzone}
          </Grid>
          <Grid item xs={3} md={4}>
            <Box display="flex" height="100%" alignItems="end">
              {inputVariantButton}
            </Box>
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid item xs={9} md={8}>
          {errorDisplay}
        </Grid>
      </Grid>
      {value && !finalError && (
        <Box
          mt={{ xs: '8', md: '16' }}
          width={{ xs: 96, md: 144 }}
          height={{ xs: 96, md: 144 }}
          testId={`${testId}-preview`}
        >
          <Box position="relative">
            {!disabled && (
              <Box position="absolute" top={7} right={8} zIndex={1}>
                <Button
                  size="extraSmall"
                  variant="quaternaryAccent"
                  onClick={onRemove}
                  testId={`${testId}-clear`}
                  icon={
                    <Icon
                      name="cancel"
                      size="16"
                      color="original.brand.primary"
                    />
                  }
                />
              </Box>
            )}
            <Tooltip title={inputText} placement="bottom">
              <Box
                position="absolute"
                bottom={1}
                right={1}
                zIndex={1}
                left={1}
                bgcolor="bg.greyscale.primary"
                py={{ xs: '2', md: '4' }}
                px="12"
                borderBottomLeftRadius="l"
                borderBottomRightRadius="l"
              >
                <EllipsisText rows={1}>
                  <Typography variant="caption">{inputText}</Typography>
                </EllipsisText>
              </Box>
            </Tooltip>
            <Paper>
              <FilePreview
                shape="square"
                onLoad={onLoad}
                clickable
                withZoom={withZoom}
              />
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
};
